import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import ArticleAction from "../../components/partials/article/ArticleAction";
import type { Article, SearchArticleParams } from "../../services/webServices/article/types";
import { getArticles } from "../../services/webServices/article/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "published", label: "منتشر شده" },
  { value: "unpublished", label: "در انتظار تایید" }
];

export default function Article() {

  const [data, setData] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchArticle = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchArticleParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    if (searchValue) params.search = searchValue;

    switch (filterValue) {
      case "published":
        params.is_published = true;
        break;
      case "unpublished":
        params.is_published = false;
        break;
    }

    const response = await getArticles({ params });

    const list = Array.isArray(response)
      ? response.map((item) => ({
          ...item,
          full_name: item.user.full_name,
        }))
      : [];

    setData(list);

    if (list.length < PAGE_SIZE) {
      const calculatedTotal = (currentPage - 1) * PAGE_SIZE + list.length;
      setTotalItems(calculatedTotal);
    } else {
      setTotalItems(currentPage * PAGE_SIZE + 1);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, searchValue, filterValue]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row:  Article) => {
    window.open(`https://sepehrjou.ir/article/dja-${row.id}/`, "_blank");
  };

  const handleRenderActions = (row:  Article) => {
    return (
      <ArticleAction
        article={row}
        onActionSuccessed={() => fetchArticle()}
      />
    );
  };

  const columns: Column<Article>[] = [
    {
      key: "title",
      header: "عنوان",
      align: "right",
      sortable: true,
      width: "45%",
      accessor: (row) => row.title,
    },
    {
      key: "full_name",
      header: "نویسنده",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <img
            className="h-[20px] w-[20px] rounded-full object-cover"
            src={process.env.REACT_APP_File_URL + row.user.avatar || "/images/avatar-alt.jpg"}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/avatar-alt.jpg";
            }}
          />
          <span>{row.user?.full_name || "بدون نام"}</span>
        </div>
      ),
    },
    {
      key: "created_at",
      header: "زمان انتشار",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        </div>
      ),
    }
  ];

  return (
    <div>
      <DataTable
        title="لیست مقالات"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری مقاله ها..."
        emptyText="مقاله‌ای یافت نشد"

        searchable={true}
        searchPlaceholder="عنوان"
        searchKeys={["title"]}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}

        filterable={true}
        filterPlaceholder="همه مقالات"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}

        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}

        onRowClick={handleOnRowClick}
        renderActions={handleRenderActions}
      />
    </div>
  );
};